import { useRef, useState } from 'react'
import { useParams } from 'react-router-dom'

import { createPet } from '@components/pet_form/helper'
import { useBookingContext } from '@providers/booking'
import { TBookingPet } from '@providers/booking/types'
import { useSessionContext } from '@providers/session'
import { isUserLoggedIn } from '@providers/session/helpers'
import { ClientLocationPetProfileService } from 'service-api/src/services/client_location_pet_profile.service'
import { ClientLocationUserProfileService } from 'service-api/src/services/client_location_user_profiles.service'
import { PetClientDto } from 'service-api/src/types/api'

const createNewPet = async (locationName: string, pet: TBookingPet, lupId: string, specieId: string) => {
  const result: PetClientDto | unknown = await createPet(
    {
      id: '',
      displayName: pet.name,
      breed: { id: pet.breedId },
      weight: pet.weight,
      birthdate: pet.birthdate || '',
      sex: pet.sex || '',
      altered: `${pet.altered}` || '',
      locationSpeciesId: specieId || '',
    },
    locationName,
    lupId
  )
  return result
}

export const usePetFamilies = () => {
  const { accountName: locationName = '', serviceTypeName = '' } = useParams()
  const bookingCtx = useBookingContext()
  const sessionCtx = useSessionContext()
  const isLoading = useRef(false)
  const alreadyRun = useRef(false)
  const [hasPets, setHasPets] = useState<boolean>(false)
  const [continueBooking, setContinueBooking] = useState<boolean>(false)
  const lupId = sessionCtx.state.user?.lupId || ''

  if (serviceTypeName === 'packages') {
    return { isLoading: false, hasPets: false, continueBooking: false }
  }

  if (!bookingCtx.state.isNewCustomer || !isUserLoggedIn(sessionCtx.state.user)) {
    return { isLoading: isLoading.current, hasPets, continueBooking }
  }

  if (hasPets || alreadyRun.current) {
    return { isLoading: isLoading.current, hasPets, continueBooking }
  }

  alreadyRun.current = true
  // setIsLoading(true)
  isLoading.current = true

  ClientLocationPetProfileService.list(locationName, lupId).then(async (lpp) => {
    if (lpp.results.length > 0) {
      setHasPets(true)
      isLoading.current = false
      return
    }

    const groups = bookingCtx.state.cart.groups
    let groupIdx = 0
    for (const group of groups) {
      const pets = group.pets || []
      let petIdx = 0
      for (const pet of pets) {
        const newPet = await createNewPet(locationName, pet, lupId, group.specieId)
        if (newPet) {
          // update pet in both cart and group to avoid override when pets are regrouped
          bookingCtx.dispatch({
            type: 'SET_PET_CART_FIELD',
            payload: {
              groupIdx,
              petIdx,
              fieldName: 'id',
              value: (newPet as PetClientDto).id,
            },
          })
          bookingCtx.dispatch({
            type: 'SET_PET_SEARCH_FIELD',
            payload: {
              groupIdx,
              petIdx,
              fieldName: 'id',
              value: (newPet as PetClientDto).id,
            },
          })
        }
        petIdx++
      }
      groupIdx++
    }

    const lupData = await ClientLocationUserProfileService.get(locationName, lupId)
    bookingCtx.dispatch({
      type: 'SET_PAYMENT_CONTACT',
      payload: {
        value: lupData,
      },
    })

    isLoading.current = false
    setContinueBooking(true)
  })

  return {
    isLoading: isLoading.current,
    hasPets,
    continueBooking,
  }
}
